import axios from 'axios'
import { debounce } from 'lodash'
import { appUrls } from '../config/ajax'
import Qs from 'qs'

class ArtistSearch {
  private input: HTMLInputElement
  private resultsContainer: HTMLElement
  private clearButton: HTMLButtonElement
  private onChangeCallback: () => void
  private results: string[] = []
  private isDev: boolean = false

  constructor(onChangeCallback: () => void, isDev: boolean = false) {
    this.input = document.querySelector('#artist-search')!
    this.resultsContainer = document.querySelector('#artist-search-results')!
    this.clearButton = document.querySelector('#artist-search-clear')!
    this.onChangeCallback = onChangeCallback
    this.isDev = isDev

    this.init()
  }

  private init(): void {
    this.input.addEventListener('input', this.debouncedHandleInput)
    this.input.addEventListener('keydown', this.handleKeydown)
    this.clearButton.addEventListener('click', this.handleClear)
    document.addEventListener('click', this.handleClickOutside)
    this.toggleClearButton()
  }

  private debouncedHandleInput = debounce(() => this.handleInput(), 300, { maxWait: 500 })

  private async handleInput(): Promise<void> {
    const query = this.input.value.trim()
    this.toggleClearButton()

    if (query.length < 2) {
      this.clearResults()
      return
    }

    this.input.classList.add('animate-inputLoading')

    try {
      const results = this.isDev ? await this.mockSearch(query) : await this.fetchArtists(query)
      this.results = results.slice(0, 8)
      this.renderResults()
    } catch (error) {
      console.error('Artist search error:', error)
      this.clearResults()
    } finally {
      this.input.classList.remove('animate-inputLoading')
    }
  }

  private async fetchArtists(artist: string): Promise<string[]> {
    const data = {
      action: 'fetch_artists',
      nonce: WP_AJAX.wpRestNonce,
      artist,
    }

    const response = await axios.post<string[]>(String(appUrls.ajax), Qs.stringify(data))
    return response.data
  }

  private async mockSearch(query: string): Promise<string[]> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const allBands = [
          'Chimaira',
          'Gojira',
          'Lamb of God',
          'Trivium',
          'In Flames',
          'Machine Head',
          'Arch Enemy',
          'Behemoth',
          'Slipknot',
          'Parkway Drive',
        ]
        const filtered = allBands.filter((name) => name.toLowerCase().includes(query.toLowerCase()))
        resolve(filtered.slice(0, 8))
      }, 350)
    })
  }

  private renderResults(): void {
    const wasHidden = this.resultsContainer.classList.contains('hidden')
    this.resultsContainer.innerHTML = ''

    if (this.results.length === 0) {
      const empty = document.createElement('div')
      empty.className = 'px-4 py-2 text-sm text-gray'
      empty.textContent = 'No results...'
      this.resultsContainer.appendChild(empty)
    } else {
      this.results.forEach((band) => {
        const item = document.createElement('div')
        item.className = 'px-4 py-2 cursor-pointer hover:bg-gold/10 text-sm text-charcoal'
        item.textContent = band
        item.addEventListener('click', () => {
          this.selectArtist(band)
        })
        this.resultsContainer.appendChild(item)
      })
    }

    if (wasHidden) {
      this.resultsContainer.classList.remove('hidden')
      this.resultsContainer.classList.remove('animate-fadeIn')
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.resultsContainer.classList.add('animate-fadeIn')
        })
      })
    }
  }

  private handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      const value = this.input.value.trim()
      if (value) {
        e.preventDefault()
        this.selectArtist(value)
      }
    }
  }

  private selectArtist(band: string): void {
    this.input.value = band
    this.updateUrlParam('artist', band)
    this.clearResults()
    this.input.blur()
    this.toggleClearButton()
    this.onChangeCallback()
  }

  private handleClickOutside = (e: MouseEvent): void => {
    const target = e.target as HTMLElement
    const clickedOutside = !this.input.contains(target) && !this.resultsContainer.contains(target)
    if (clickedOutside) this.clearResults()
  }

  private handleClear = (): void => {
    this.input.value = ''
    this.clearResults()
    this.input.focus()
    this.toggleClearButton()
    // No fetchReleases call here
  }

  private clearResults(): void {
    this.results = []
    this.resultsContainer.innerHTML = ''
    this.resultsContainer.classList.add('hidden')
    this.resultsContainer.classList.remove('animate-fadeIn')
    this.input.classList.remove('animate-inputLoading')
  }

  private toggleClearButton(): void {
    const hasText = this.input.value.trim() !== ''
    this.clearButton.classList.toggle('hidden', !hasText)
    this.clearButton.classList.toggle('flex', hasText)
  }

  private updateUrlParam(key: string, value: string): void {
    const url = new URL(window.location.href)
    url.searchParams.set(key, value)
    window.history.replaceState({}, '', url.toString())
  }
}

export default ArtistSearch
